import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import {
    Alert,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    Tab,
    Tabs,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import VolunteerActivismOutlinedIcon from "@mui/icons-material/VolunteerActivismOutlined";
import { getApiBaseUrl } from "../../../config/api";
import { getSchoolIdForUser } from "../../../utils/userContext";
import { getAllSclasses } from "../../../redux/sclassRelated/sclassHandle";
import Popup from "../../../components/Popup";
import {
    FinancePageContainer,
    FinancePageHeader,
    FinancePanel,
    FinanceHeadCell,
    FinanceTableRow,
    financeTableContainerSx,
} from "./financeUi";
import {
    FEE_AID_TYPES,
    createFeeAid,
    fetchFeeAids,
    fetchSubsidyReport,
    revokeFeeAid,
    updateFeeAid,
} from "../../../utils/feeAidApi";

const TERMS = ["", "Term 1", "Term 2", "Term 3", "Full Year"];
const y = new Date().getFullYear();
const YEARS = ["", `${y - 1}/${y}`, `${y}/${y + 1}`, `${y + 1}/${y + 2}`];
const formatMWK = (n) => "MWK " + Number(n || 0).toLocaleString();

const EMPTY = {
    studentId: "",
    aidType: "Scholarship",
    title: "",
    amount: "",
    percentOff: "",
    academicYear: `${y}/${y + 1}`,
    term: "",
    ministryReference: "",
    sponsorName: "",
    notes: "",
};

const FeeAidManagement = () => {
    const dispatch = useDispatch();
    const { currentUser } = useSelector((s) => s.user);
    const { sclassesList } = useSelector((s) => s.sclass);
    const schoolId = getSchoolIdForUser(currentUser);

    const [tab, setTab] = useState(0);
    const [aids, setAids] = useState([]);
    const [subsidyReport, setSubsidyReport] = useState(null);
    const [students, setStudents] = useState([]);
    const [filterClass, setFilterClass] = useState("");
    const [filterType, setFilterType] = useState("");
    const [loading, setLoading] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(EMPTY);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState("");
    const [showPopup, setShowPopup] = useState(false);

    useEffect(() => {
        if (schoolId) dispatch(getAllSclasses(schoolId, "Sclass"));
    }, [dispatch, schoolId]);

    const loadStudents = useCallback(async () => {
        if (!filterClass) {
            setStudents([]);
            return;
        }
        try {
            const { data } = await axios.get(`${getApiBaseUrl()}/Sclass/Students/${filterClass}`);
            setStudents(Array.isArray(data) ? data : []);
        } catch {
            setStudents([]);
        }
    }, [filterClass]);

    const loadAids = useCallback(async () => {
        if (!schoolId) return;
        setLoading(true);
        try {
            const filters = { status: "Active" };
            if (filterType) filters.aidType = filterType;
            const rows = await fetchFeeAids(schoolId, filters);
            setAids(rows);
        } catch (err) {
            setMessage(err.response?.data?.message || err.message || "Could not load aids.");
            setShowPopup(true);
        } finally {
            setLoading(false);
        }
    }, [schoolId, filterType]);

    const loadSubsidy = useCallback(async () => {
        if (!schoolId) return;
        try {
            const report = await fetchSubsidyReport(schoolId, {});
            setSubsidyReport(report);
        } catch {
            setSubsidyReport(null);
        }
    }, [schoolId]);

    useEffect(() => {
        loadStudents();
    }, [loadStudents]);

    useEffect(() => {
        loadAids();
        loadSubsidy();
    }, [loadAids, loadSubsidy]);

    const studentLabel = (row) => {
        const s = row.student;
        return s ? `${s.name} (${s.rollNum})` : row.studentId;
    };

    const openCreate = () => {
        setEditing(null);
        setForm({ ...EMPTY, studentId: students[0]?._id || students[0]?.id || "" });
        setDialogOpen(true);
    };

    const openEdit = (row) => {
        setEditing(row);
        setForm({
            studentId: row.studentId,
            aidType: row.aidType,
            title: row.title || "",
            amount: String(row.amount ?? ""),
            percentOff: row.percentOff != null ? String(row.percentOff) : "",
            academicYear: row.academicYear || "",
            term: row.term || "",
            ministryReference: row.ministryReference || "",
            sponsorName: row.sponsorName || "",
            notes: row.notes || "",
        });
        setDialogOpen(true);
    };

    const handleSave = async () => {
        if (!form.studentId) {
            setMessage("Select a student.");
            setShowPopup(true);
            return;
        }
        setSaving(true);
        try {
            const payload = {
                ...form,
                schoolId,
                createdById: currentUser?._id || currentUser?.id,
            };
            if (editing) {
                await updateFeeAid(editing._id || editing.id, payload);
                setMessage("Aid updated — fee balances recalculated.");
            } else {
                await createFeeAid(payload);
                setMessage("Aid created — applied to matching fee records.");
            }
            setDialogOpen(false);
            setShowPopup(true);
            await loadAids();
            await loadSubsidy();
        } catch (err) {
            setMessage(err.response?.data?.message || err.message || "Could not save.");
            setShowPopup(true);
        } finally {
            setSaving(false);
        }
    };

    const handleRevoke = async (row) => {
        if (!window.confirm(`Revoke ${row.aidType} for ${studentLabel(row)}?`)) return;
        try {
            await revokeFeeAid(row._id || row.id);
            setMessage("Aid revoked.");
            setShowPopup(true);
            await loadAids();
            await loadSubsidy();
        } catch (err) {
            setMessage(err.response?.data?.message || err.message || "Could not revoke.");
            setShowPopup(true);
        }
    };

    const aidTypeColor = (type) => {
        if (type === "GovSubsidy") return "info";
        if (type === "Scholarship") return "success";
        if (type === "Bursary") return "secondary";
        return "warning";
    };

    const filteredAids = useMemo(() => {
        if (!filterClass) return aids;
        return aids.filter((a) => String(a.student?.sclassName || "") === String(filterClass));
    }, [aids, filterClass]);

    return (
        <FinancePageContainer>
            <FinancePageHeader
                title="Waivers, scholarships & bursaries"
                subtitle="Manage fee reductions, government subsidies, and sponsor awards. Amounts apply automatically to matching term bills."
                icon={<VolunteerActivismOutlinedIcon />}
            />

            <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
                <Tab label="Active aids" />
                <Tab label="Government subsidy report" />
            </Tabs>

            {tab === 0 ? (
                <FinancePanel
                    title="Student fee aids"
                    action={
                        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate} disabled={!filterClass}>
                            Add aid
                        </Button>
                    }
                >
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                        <Grid item xs={12} sm={4}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Class (to pick students)</InputLabel>
                                <Select label="Class (to pick students)" value={filterClass} onChange={(e) => setFilterClass(e.target.value)}>
                                    <MenuItem value="">All classes</MenuItem>
                                    {(sclassesList || []).map((c) => (
                                        <MenuItem key={c._id || c.id} value={c._id || c.id}>
                                            {c.sclassName}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Aid type</InputLabel>
                                <Select label="Aid type" value={filterType} onChange={(e) => setFilterType(e.target.value)}>
                                    <MenuItem value="">All types</MenuItem>
                                    {FEE_AID_TYPES.map((t) => (
                                        <MenuItem key={t} value={t}>
                                            {t}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>

                    <TableContainer sx={financeTableContainerSx}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <FinanceHeadCell>Student</FinanceHeadCell>
                                    <FinanceHeadCell>Type</FinanceHeadCell>
                                    <FinanceHeadCell>Title</FinanceHeadCell>
                                    <FinanceHeadCell align="right">Amount / %</FinanceHeadCell>
                                    <FinanceHeadCell>Year / Term</FinanceHeadCell>
                                    <FinanceHeadCell>Reference</FinanceHeadCell>
                                    <FinanceHeadCell align="right">Actions</FinanceHeadCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredAids.length === 0 ? (
                                    <FinanceTableRow>
                                        <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                                            {loading ? "Loading…" : "No active aids. Select a class and add a scholarship, bursary, waiver, or government subsidy."}
                                        </TableCell>
                                    </FinanceTableRow>
                                ) : (
                                    filteredAids.map((row) => (
                                        <FinanceTableRow key={row._id || row.id}>
                                            <TableCell>{studentLabel(row)}</TableCell>
                                            <TableCell>
                                                <Chip size="small" color={aidTypeColor(row.aidType)} label={row.aidType} />
                                            </TableCell>
                                            <TableCell>{row.title || "—"}</TableCell>
                                            <TableCell align="right">
                                                {row.percentOff != null && Number(row.percentOff) > 0
                                                    ? `${row.percentOff}%`
                                                    : formatMWK(row.amount)}
                                            </TableCell>
                                            <TableCell>
                                                {row.academicYear || "Any"} {row.term ? `· ${row.term}` : ""}
                                            </TableCell>
                                            <TableCell>{row.ministryReference || row.sponsorName || "—"}</TableCell>
                                            <TableCell align="right">
                                                <Button size="small" onClick={() => openEdit(row)}>
                                                    Edit
                                                </Button>
                                                <Button size="small" color="error" onClick={() => handleRevoke(row)}>
                                                    Revoke
                                                </Button>
                                            </TableCell>
                                        </FinanceTableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </FinancePanel>
            ) : (
                <FinancePanel title="Government subsidy tracking">
                    {subsidyReport ? (
                        <Stack spacing={2}>
                            <Grid container spacing={2}>
                                <Grid item xs={6} sm={3}>
                                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                                        <Typography variant="caption" color="text.secondary">
                                            Subsidy applied
                                        </Typography>
                                        <Typography fontWeight={700}>{formatMWK(subsidyReport.totalSubsidyApplied)}</Typography>
                                    </Paper>
                                </Grid>
                                <Grid item xs={6} sm={3}>
                                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                                        <Typography variant="caption" color="text.secondary">
                                            Committed programs
                                        </Typography>
                                        <Typography fontWeight={700}>{formatMWK(subsidyReport.totalSubsidyCommitted)}</Typography>
                                    </Paper>
                                </Grid>
                                <Grid item xs={6} sm={3}>
                                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                                        <Typography variant="caption" color="text.secondary">
                                            Students with subsidy
                                        </Typography>
                                        <Typography fontWeight={700}>{subsidyReport.studentCount ?? 0}</Typography>
                                    </Paper>
                                </Grid>
                            </Grid>
                            <Alert severity="info" sx={{ borderRadius: 2 }}>
                                Register government bursaries under aid type <strong>GovSubsidy</strong> with ministry reference numbers for EMIS reconciliation.
                            </Alert>
                        </Stack>
                    ) : (
                        <Typography color="text.secondary">No subsidy data yet.</Typography>
                    )}
                </FinancePanel>
            )}

            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>{editing ? "Edit fee aid" : "Add fee aid"}</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Student</InputLabel>
                            <Select label="Student" value={form.studentId} onChange={(e) => setForm((f) => ({ ...f, studentId: e.target.value }))}>
                                {students.map((s) => (
                                    <MenuItem key={s._id || s.id} value={s._id || s.id}>
                                        {s.rollNum} — {s.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl fullWidth size="small">
                            <InputLabel>Aid type</InputLabel>
                            <Select label="Aid type" value={form.aidType} onChange={(e) => setForm((f) => ({ ...f, aidType: e.target.value }))}>
                                {FEE_AID_TYPES.map((t) => (
                                    <MenuItem key={t} value={t}>
                                        {t}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <TextField size="small" label="Title" fullWidth value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} />
                        <Stack direction="row" spacing={2}>
                            <TextField size="small" label="Fixed amount (MWK)" type="number" value={form.amount} onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))} sx={{ flex: 1 }} />
                            <TextField size="small" label="Or % off bill" type="number" value={form.percentOff} onChange={(e) => setForm((f) => ({ ...f, percentOff: e.target.value }))} sx={{ flex: 1 }} />
                        </Stack>
                        <Stack direction="row" spacing={2}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Academic year</InputLabel>
                                <Select label="Academic year" value={form.academicYear} onChange={(e) => setForm((f) => ({ ...f, academicYear: e.target.value }))}>
                                    {YEARS.map((y) => (
                                        <MenuItem key={y || "any"} value={y}>
                                            {y || "Any year"}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <FormControl fullWidth size="small">
                                <InputLabel>Term</InputLabel>
                                <Select label="Term" value={form.term} onChange={(e) => setForm((f) => ({ ...f, term: e.target.value }))}>
                                    {TERMS.map((t) => (
                                        <MenuItem key={t || "any"} value={t}>
                                            {t || "Any term"}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Stack>
                        {form.aidType === "GovSubsidy" ? (
                            <TextField size="small" label="Ministry / EMIS reference" fullWidth value={form.ministryReference} onChange={(e) => setForm((f) => ({ ...f, ministryReference: e.target.value }))} />
                        ) : (
                            <TextField size="small" label="Sponsor name" fullWidth value={form.sponsorName} onChange={(e) => setForm((f) => ({ ...f, sponsorName: e.target.value }))} />
                        )}
                        <TextField size="small" label="Notes" fullWidth multiline minRows={2} value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
                    <Button variant="contained" disabled={saving} onClick={handleSave}>
                        {saving ? "Saving…" : "Save"}
                    </Button>
                </DialogActions>
            </Dialog>

            <Popup message={message} setShowPopup={setShowPopup} showPopup={showPopup} />
        </FinancePageContainer>
    );
};

export default FeeAidManagement;
